/**
 * T2-6634 / T2-6959 / T2-6963: Graduation Year + "Licensed Since" month+year helpers (client).
 * Accepted range: 1960 .. current calendar year. Licensed year cannot precede graduation.
 * Month (1-12) is required whenever year is required; combined month+year cannot be future.
 * N/A path: firstLicensedYear/Month stay unset; callers send firstLicensedNotApplicable: true.
 */

/** Validation floor - kept at 1960 so historical rows (and the API mirror) stay valid. */
export const MIN_CREDENTIAL_YEAR = 1960;

/** Dropdown floor (T2-6963) - nobody in the population graduated or was licensed before this. */
export const MIN_CREDENTIAL_OPTION_YEAR = 1970;

/**
 * Base wording for the month+year pair - replaces the split Month/Year labels (T2-6963).
 * Dental Assistants register/certify rather than licence, so they get their own wording.
 */
export const LICENSED_SINCE_BASE = 'Licensed';
export const REGISTERED_SINCE_BASE = 'Registered / Certified';

export const CREDENTIAL_MONTH_OPTIONS: ReadonlyArray<{ value: number; label: string }> = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

export const getCurrentCredentialYear = (): number => new Date().getFullYear();

export const getCurrentCredentialMonth = (): number => new Date().getMonth() + 1;

export const getCredentialYearOptions = (
  minYear: number = MIN_CREDENTIAL_OPTION_YEAR,
  maxYear: number = getCurrentCredentialYear(),
): number[] => {
  const years: number[] = [];
  for (let y = maxYear; y >= minYear; y -= 1) {
    years.push(y);
  }
  return years;
};

/**
 * Year options that still contain any already-stored year older than the dropdown floor,
 * so Talent with a legacy value are not trapped in the required confirmation modal.
 */
export const getCredentialYearOptionsIncluding = (
  ...storedYears: Array<number | string | null | undefined>
): number[] => {
  const options = getCredentialYearOptions();
  const oldest = options[options.length - 1];
  const legacy = storedYears
    .map((value) => Number.parseInt(String(value ?? ''), 10))
    .filter((year) => Number.isInteger(year) && year >= MIN_CREDENTIAL_YEAR && year < oldest);

  if (!legacy.length) {
    return options;
  }

  return Array.from(new Set([...options, ...legacy])).sort((a, b) => b - a);
};

export const hasDisplayableCredentialYear = (year: number | string | null | undefined): boolean => {
  if (year === null || year === undefined || year === '') {
    return false;
  }
  const numeric = Number(year);
  return Number.isFinite(numeric) && numeric > 0;
};

export const formatCredentialMonthName = (month: number | string | null | undefined): string | null => {
  if (month === null || month === undefined || month === '') {
    return null;
  }
  const numeric = Number(month);
  if (!Number.isInteger(numeric) || numeric < 1 || numeric > 12) {
    return null;
  }
  return CREDENTIAL_MONTH_OPTIONS[numeric - 1].label;
};

export const formatGraduatedLabel = (year: number | string | null | undefined): string | null => {
  if (!hasDisplayableCredentialYear(year)) {
    return null;
  }
  return `Graduated: ${year}`;
};

/**
 * DO-facing display. With month: "First Licensed / Certified: June 2021".
 * Year-only (historical): "First Licensed / Certified: 2021". Never shows 0/empty month.
 */
export const formatFirstLicensedLabel = (
  year: number | string | null | undefined,
  month?: number | string | null,
): string | null => {
  if (!hasDisplayableCredentialYear(year)) {
    return null;
  }
  const monthName = formatCredentialMonthName(month);
  if (monthName) {
    return `First Licensed / Certified: ${monthName} ${year}`;
  }
  return `First Licensed / Certified: ${year}`;
};

/** DO-facing pair of display strings (null when the year should not render). */
export const formatTalentCredentialYears = (
  hyg: {
    graduationYear?: number | string | null;
    firstLicensedYear?: number | string | null;
    firstLicensedMonth?: number | string | null;
  } | null | undefined,
): { graduated: string | null; firstLicensed: string | null } => ({
  graduated: formatGraduatedLabel(hyg?.graduationYear),
  firstLicensed: formatFirstLicensedLabel(hyg?.firstLicensedYear, hyg?.firstLicensedMonth),
});

/**
 * Country the credential was issued in, for the field label (T2-6963).
 * Talent country is 0 = CA / 1 = US numerically, or 'CA' / 'US' on countryStr.
 * Returns null when unknown so the label falls back to the country-free wording.
 */
export const getCredentialCountryName = (country: number | string | null | undefined): string | null => {
  if (country === null || country === undefined || country === '') {
    return null;
  }
  if (country === 0 || country === 1) {
    return country === 1 ? 'USA' : 'Canada';
  }
  const asString = String(country).trim().toUpperCase();
  if (asString === 'US' || asString === 'USA' || asString === '1') {
    return 'USA';
  }
  if (asString === 'CA' || asString === 'CANADA' || asString === '0') {
    return 'Canada';
  }
  return null;
};

/**
 * Signup / confirmation label for the month+year pair (T2-6963).
 * Type 1 (Dental Assistant) registers / certifies; Hygienists (0) and Dentists (3) licence.
 * Unknown types fall back to the licence wording.
 */
export const getLicensedSinceFieldLabel = (
  hygienistType: number | null | undefined,
  country?: number | string | null,
): string => {
  const base = hygienistType === 1 ? REGISTERED_SINCE_BASE : LICENSED_SINCE_BASE;
  const countryName = getCredentialCountryName(country);
  return countryName ? `${base} in ${countryName} Since` : `${base} Since`;
};

/**
 * Opt-out option shown at the bottom of the MONTH dropdown for Dental Assistants (T2-6963),
 * replacing the separate "does not apply to me" checkbox. Selecting it hides the year field.
 */
export const NOT_APPLICABLE_VALUE = 'not-applicable';
export const NOT_APPLICABLE_LABEL = 'Not Registered / Certified';

/** True when the Talent type may opt out of a first licensed / certified date in the modal. */
export const canOptOutOfFirstLicensed = (hygienistType: number | null | undefined): boolean => (
  hygienistType === 1
);

/** Matching sentence fragment for the modal intro, e.g. "when you were first licensed". */
export const getFirstCredentialPhrase = (hygienistType: number | null | undefined): string => (
  hygienistType === 1 ? 'first registered / certified' : 'first licensed'
);

/** Helper copy shown under the Licensed Since row. */
export const LICENSED_SINCE_HINT = 'The month and year you were first licensed, registered, or certified - not a renewal.';

/** Helper copy shown under the Graduation Year field. */
export const GRADUATION_YEAR_HINT = 'The year you completed your relevant dental education or training program.';

/** True when the Talent path typically has no professional licence/certificate (admin, or unlicensed DA). */
export const isFirstLicensedNotApplicablePath = (
  hygienistType: number | null | undefined,
  licensed?: boolean | null,
): boolean => {
  if (hygienistType === 2) {
    return true; // Dental Admin / Manager
  }
  if (hygienistType === 1 && licensed === false) {
    return true; // Unlicensed Dental Assistant
  }
  return false;
};

export type EducationCredentialsValidation =
  | {
      ok: true;
      graduationYear: number;
      firstLicensedYear: number | null;
      firstLicensedMonth: number | null;
      firstLicensedNotApplicable: boolean;
    }
  | { ok: false; message: string; field?: 'graduationYear' | 'firstLicensedYear' | 'firstLicensedMonth' };

const parseYearInput = (value: unknown): number | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || value === '') {
    return null;
  }
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isInteger(parsed) || Number.isNaN(parsed)) {
    return undefined;
  }
  return parsed;
};

const parseMonthInput = (value: unknown): number | null | undefined => {
  if (value === undefined) {
    return undefined;
  }
  if (value === null || value === '') {
    return null;
  }
  const parsed = Number.parseInt(String(value), 10);
  if (!Number.isInteger(parsed) || Number.isNaN(parsed) || parsed < 1 || parsed > 12) {
    return undefined;
  }
  return parsed;
};

const isValidCredentialYear = (year: number, currentYear: number): boolean =>
  Number.isInteger(year) && year >= MIN_CREDENTIAL_YEAR && year <= currentYear;

const isLicensedMonthYearNotFuture = (
  year: number,
  month: number,
  currentYear: number,
  currentMonth: number,
): boolean => {
  if (year < currentYear) return true;
  if (year > currentYear) return false;
  return month <= currentMonth;
};

/**
 * Client-side mirror of API validateEducationCredentials.
 */
export const validateEducationCredentials = ({
  graduationYear: rawGraduationYear,
  firstLicensedYear: rawFirstLicensedYear,
  firstLicensedMonth: rawFirstLicensedMonth,
  firstLicensedNotApplicable = false,
  allowNotApplicable = false,
}: {
  graduationYear?: unknown;
  firstLicensedYear?: unknown;
  firstLicensedMonth?: unknown;
  firstLicensedNotApplicable?: boolean;
  /** True only where the UI actually offers an opt-out, so the copy never names a missing control. */
  allowNotApplicable?: boolean;
}): EducationCredentialsValidation => {
  const currentYear = getCurrentCredentialYear();
  const missingYearMessage = allowNotApplicable
    ? 'Please select the year you were first licensed or certified, or choose "Not Registered / Certified"'
    : 'Please select the year you were first licensed or certified';
  const currentMonth = getCurrentCredentialMonth();
  const graduationYear = parseYearInput(rawGraduationYear);
  const firstLicensedNotApplicableBool = Boolean(firstLicensedNotApplicable);

  if (graduationYear === undefined || graduationYear === null) {
    return { ok: false, message: 'Please select your graduation year', field: 'graduationYear' };
  }
  if (!isValidCredentialYear(graduationYear, currentYear)) {
    return {
      ok: false,
      message: `Graduation Year must be between ${MIN_CREDENTIAL_YEAR} and ${currentYear}`,
      field: 'graduationYear',
    };
  }

  if (firstLicensedNotApplicableBool) {
    return {
      ok: true,
      graduationYear,
      firstLicensedYear: null,
      firstLicensedMonth: null,
      firstLicensedNotApplicable: true,
    };
  }

  if (rawFirstLicensedYear === undefined) {
    return {
      ok: false,
      message: missingYearMessage,
      field: 'firstLicensedYear',
    };
  }

  const firstLicensedYear = parseYearInput(rawFirstLicensedYear);
  if (firstLicensedYear === undefined) {
    return {
      ok: false,
      message: 'Please select a valid year for when you were first licensed or certified',
      field: 'firstLicensedYear',
    };
  }
  if (firstLicensedYear === null) {
    return {
      ok: false,
      message: missingYearMessage,
      field: 'firstLicensedYear',
    };
  }
  if (!isValidCredentialYear(firstLicensedYear, currentYear)) {
    return {
      ok: false,
      message: `The year you were first licensed or certified must be between ${MIN_CREDENTIAL_YEAR} and ${currentYear}`,
      field: 'firstLicensedYear',
    };
  }
  if (firstLicensedYear < graduationYear) {
    return {
      ok: false,
      message: 'The year you were first licensed or certified cannot be earlier than your Graduation Year',
      field: 'firstLicensedYear',
    };
  }

  if (rawFirstLicensedMonth === undefined || rawFirstLicensedMonth === null || rawFirstLicensedMonth === '') {
    return {
      ok: false,
      message: 'Please select the month you were first licensed or certified',
      field: 'firstLicensedMonth',
    };
  }
  const firstLicensedMonth = parseMonthInput(rawFirstLicensedMonth);
  if (firstLicensedMonth === undefined || firstLicensedMonth === null) {
    return {
      ok: false,
      message: 'Please select a valid month for when you were first licensed or certified',
      field: 'firstLicensedMonth',
    };
  }
  if (!isLicensedMonthYearNotFuture(firstLicensedYear, firstLicensedMonth, currentYear, currentMonth)) {
    return {
      ok: false,
      message: 'The month and year you were first licensed or certified cannot be in the future',
      field: 'firstLicensedMonth',
    };
  }

  return {
    ok: true,
    graduationYear,
    firstLicensedYear,
    firstLicensedMonth,
    firstLicensedNotApplicable: false,
  };
};
